import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { installRecoveryTracker } from './recoverySession'
// Importing this module captures + scrubs a recovery URL in history before
// createClient runs, and reports whether this load is the reset route.
import { wasRecoveryRouteLoad, RECOVERY_AUTH_OPTIONS } from './recoveryHandoff'

// Check for environment variables (prioritize non-prefixed for Netlify, fallback to REACT_APP_ for local dev)
const supabaseUrl = process.env.SUPABASE_URL || process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.SUPABASE_ANON_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  if (process.env.NODE_ENV === 'production') {
    // Production log stays generic: no env names, URLs or key details.
    console.warn('RidePulse service is currently unavailable.');
  } else {
    const errorMsg = 'Missing Supabase environment variables';
    console.error(errorMsg, {
      hasUrl: !!supabaseUrl,
      hasKey: !!supabaseAnonKey,
      nodeEnv: process.env.NODE_ENV,
      allEnvKeys: Object.keys(process.env).filter(key => key.includes('SUPABASE'))
    });
    throw new Error(`${errorMsg}. Check your .env file and ensure all required variables are set.`);
  }
}

// Validate URL format (only if we have a URL)
if (supabaseUrl) {
  try {
    new URL(supabaseUrl);
  } catch {
    if (process.env.NODE_ENV === 'production') {
      console.warn('RidePulse service is currently unavailable.')
    } else {
      console.error('Invalid Supabase URL format:', supabaseUrl)
      throw new Error('Invalid Supabase URL format')
    }
  }
}

// On the reset route the client is ephemeral — no URL session import, no
// persistence, no refresh — so native bearer values cannot be consumed or
// invalidated by ordinary rider auth state. All other routes keep defaults.
export const supabase: SupabaseClient = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', 
  supabaseAnonKey || 'placeholder-key',
  wasRecoveryRouteLoad() ? { auth: { ...RECOVERY_AUTH_OPTIONS } } : undefined
);

// Must run before the client's URL token processing completes, or the
// PASSWORD_RECOVERY event is published before any listener exists.
installRecoveryTracker(supabase);

// Connection validation function
export const validateConnection = async (): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('waitlist')
      .select('count')
      .limit(1)
      .single()

    if (error && error.code !== 'PGRST116') {
      console.error('Supabase connection validation failed:', error)
      return false
    }
    
    return true
  } catch (error) {
    console.error('Supabase connection error:', error)
    return false
  }
}

// Rate-limited database operations wrapper
const createRateLimitedOperation = (operation: string, maxRequests = 10, windowMs = 60000) => {
  const requests: number[] = []
  
  return async (fn: () => Promise<any>) => {
    const now = Date.now()
    
    // Clean old requests outside the window
    while (requests.length > 0 && requests[0] <= now - windowMs) {
      requests.shift()
    }
    
    // Check rate limit
    if (requests.length >= maxRequests) {
      return { success: false, error: `Rate limit exceeded for ${operation}` }
    }
    
    requests.push(now)
    
    try {
      const result = await fn()
      return result
    } catch (error) {
      console.error(`Database operation failed (${operation}):`, error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown database error' 
      }
    }
  }
}

// Create rate-limited operations
const rateLimitedInsert = createRateLimitedOperation('insert', 5, 60000) // 5 per minute
const rateLimitedSelect = createRateLimitedOperation('select', 20, 60000) // 20 per minute

// Enhanced database client with validation and rate limiting
export const db = {
  // Waitlist operations with rate limiting and validation
  async waitlist_insert(data: {
    name: string
    email: string
    city: string
    state: string
    motorcycle_type: string
    source: string
    platform?: string | null
  }) {
    return rateLimitedInsert(async () => {
      // Validate input data
      if (!data.name?.trim() || !data.email?.trim()) {
        return { success: false, error: 'Name and email are required' }
      }
      
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        return { success: false, error: 'Invalid email format' }
      }
      
      // Validate connection before operation
      const isConnected = await validateConnection()
      if (!isConnected) {
        return { success: false, error: 'Database connection unavailable' }
      }
      
      const { error } = await supabase
        .from('waitlist')
        .insert([{
          name: data.name.trim(),
          email: data.email.trim().toLowerCase(),
          city: data.city.trim(),
          state: data.state.trim(),
          motorcycle_type: data.motorcycle_type || 'unknown',
          source: data.source || 'direct',
          platform: data.platform || null
        }])
      
      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'Email already exists' }
        }
        return { success: false, error: `Database error: ${error.message}` }
      }
      
      return { success: true }
    })
  },
  
  async waitlist_select(filters?: { email?: string }) {
    return rateLimitedSelect(async () => {
      // Validate connection before operation
      const isConnected = await validateConnection()
      if (!isConnected) {
        return { success: false, error: 'Database connection unavailable' }
      }
      
      let query = supabase.from('waitlist').select('*')
      
      if (filters?.email) {
        query = query.eq('email', filters.email.toLowerCase())
      }
      
      const { data, error } = await query
      
      if (error) {
        return { success: false, error: `Database error: ${error.message}` }
      }
      
      return { success: true, data }
    })
  }
}
